import {
  AppShell,
  Button,
  Group,
  Loader,
  Stack,
  TextInput,
  NumberInput,
  Switch,
  Textarea,
  Table,
  Skeleton,
  Modal,
  Paper,
  Fieldset,
  NavLink,
  Text,
  Autocomplete,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import { reset, initialize, updateField } from "../slices/form/ventConfigTab";
import {
  DeleteHostRequest,
  SaveVentRequest,
  useDeleteHostMutation,
  useGetVentQuery,
  useSaveVentMutation,
} from "../slices/api/ventsApi";
import { JSX, useEffect, useRef, useState } from "react";
import {
  displayErrorMessage,
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import { DateTimePicker } from "@mantine/dates";
import { IsBlockNav, SetBlockNav } from "../App";
import {
  IconAddNew,
  IconCustomField,
  IconDelete,
  IconEdit,
  IconEmailConfiguration,
  IconEventHome,
  IconGuests,
  IconActivities,
} from "../shared/components/Icons";
import { openConfirmationModal } from "../shared/components/Notifications";
import { formatPerm } from "..//slices/types";
import { useDisclosure } from "@mantine/hooks";
import EditHost from "./EditHost";
import CreateHost from "./CreateHost";
import { Editor } from "../shared/components/Editor";
import dayjs from "dayjs";
import {
  convertToIso8601WithLocalTimezone,
  localDateToServer,
} from "../shared/dates";
import { Link, useLocation } from "wouter";
import { LabelWithTooltipHelp } from "../shared/components/LabelWithTooltipHelp";
import { EbTooltip } from "../shared/components/EbTooltip";
import { EbActionIcon } from "../shared/components/EbActionItem";
import { TabHeader } from "../shared/components/TabHeader";
import {
  getLinkToVentCustomFields,
  getLinkToVentEmailTemplates,
  getLinkToVentGuests,
  getLinkToVentHome,
  getLinkToVentActivities,
} from "../shared/links";

const timeZones = [
  "Africa/Abidjan",
  "Africa/Algiers",
  "Africa/Bissau",
  "Africa/Cairo",
  "Africa/Casablanca",
  "Africa/Ceuta",
  "Africa/El_Aaiun",
  "Africa/Johannesburg",
  "Africa/Juba",
  "Africa/Khartoum",
  "Africa/Lagos",
  "Africa/Maputo",
  "Africa/Monrovia",
  "Africa/Nairobi",
  "Africa/Ndjamena",
  "Africa/Sao_Tome",
  "Africa/Tripoli",
  "Africa/Tunis",
  "Africa/Windhoek",
  "America/Adak",
  "America/Anchorage",
  "America/Araguaina",
  "America/Argentina/Buenos_Aires",
  "America/Argentina/Catamarca",
  "America/Argentina/Cordoba",
  "America/Argentina/Jujuy",
  "America/Argentina/La_Rioja",
  "America/Argentina/Mendoza",
  "America/Argentina/Rio_Gallegos",
  "America/Argentina/Salta",
  "America/Argentina/San_Juan",
  "America/Argentina/San_Luis",
  "America/Argentina/Tucuman",
  "America/Argentina/Ushuaia",
  "America/Asuncion",
  "America/Bahia",
  "America/Bahia_Banderas",
  "America/Barbados",
  "America/Belem",
  "America/Belize",
  "America/Boa_Vista",
  "America/Bogota",
  "America/Boise",
  "America/Cambridge_Bay",
  "America/Campo_Grande",
  "America/Cancun",
  "America/Caracas",
  "America/Cayenne",
  "America/Chicago",
  "America/Chihuahua",
  "America/Ciudad_Juarez",
  "America/Costa_Rica",
  "America/Coyhaique",
  "America/Cuiaba",
  "America/Danmarkshavn",
  "America/Dawson",
  "America/Dawson_Creek",
  "America/Denver",
  "America/Detroit",
  "America/Edmonton",
  "America/Eirunepe",
  "America/El_Salvador",
  "America/Fort_Nelson",
  "America/Fortaleza",
  "America/Glace_Bay",
  "America/Goose_Bay",
  "America/Grand_Turk",
  "America/Guatemala",
  "America/Guayaquil",
  "America/Guyana",
  "America/Halifax",
  "America/Havana",
  "America/Hermosillo",
  "America/Indiana/Indianapolis",
  "America/Indiana/Knox",
  "America/Indiana/Marengo",
  "America/Indiana/Petersburg",
  "America/Indiana/Tell_City",
  "America/Indiana/Vevay",
  "America/Indiana/Vincennes",
  "America/Indiana/Winamac",
  "America/Inuvik",
  "America/Iqaluit",
  "America/Jamaica",
  "America/Juneau",
  "America/Kentucky/Louisville",
  "America/Kentucky/Monticello",
  "America/La_Paz",
  "America/Lima",
  "America/Los_Angeles",
  "America/Maceio",
  "America/Managua",
  "America/Manaus",
  "America/Martinique",
  "America/Matamoros",
  "America/Mazatlan",
  "America/Menominee",
  "America/Merida",
  "America/Metlakatla",
  "America/Mexico_City",
  "America/Miquelon",
  "America/Moncton",
  "America/Monterrey",
  "America/Montevideo",
  "America/New_York",
  "America/Nome",
  "America/Noronha",
  "America/North_Dakota/Beulah",
  "America/North_Dakota/Center",
  "America/North_Dakota/New_Salem",
  "America/Nuuk",
  "America/Ojinaga",
  "America/Panama",
  "America/Paramaribo",
  "America/Phoenix",
  "America/Port-au-Prince",
  "America/Porto_Velho",
  "America/Puerto_Rico",
  "America/Punta_Arenas",
  "America/Rankin_Inlet",
  "America/Recife",
  "America/Regina",
  "America/Resolute",
  "America/Rio_Branco",
  "America/Santarem",
  "America/Santiago",
  "America/Santo_Domingo",
  "America/Sao_Paulo",
  "America/Scoresbysund",
  "America/Sitka",
  "America/St_Johns",
  "America/Swift_Current",
  "America/Tegucigalpa",
  "America/Thule",
  "America/Tijuana",
  "America/Toronto",
  "America/Vancouver",
  "America/Whitehorse",
  "America/Winnipeg",
  "America/Yakutat",
  "Antarctica/Casey",
  "Antarctica/Davis",
  "Antarctica/Macquarie",
  "Antarctica/Mawson",
  "Antarctica/Palmer",
  "Antarctica/Rothera",
  "Antarctica/Troll",
  "Antarctica/Vostok",
  "Asia/Almaty",
  "Asia/Amman",
  "Asia/Anadyr",
  "Asia/Aqtau",
  "Asia/Aqtobe",
  "Asia/Ashgabat",
  "Asia/Atyrau",
  "Asia/Baghdad",
  "Asia/Baku",
  "Asia/Bangkok",
  "Asia/Barnaul",
  "Asia/Beirut",
  "Asia/Bishkek",
  "Asia/Chita",
  "Asia/Colombo",
  "Asia/Damascus",
  "Asia/Dhaka",
  "Asia/Dili",
  "Asia/Dubai",
  "Asia/Dushanbe",
  "Asia/Famagusta",
  "Asia/Gaza",
  "Asia/Hebron",
  "Asia/Ho_Chi_Minh",
  "Asia/Hong_Kong",
  "Asia/Hovd",
  "Asia/Irkutsk",
  "Asia/Jakarta",
  "Asia/Jayapura",
  "Asia/Jerusalem",
  "Asia/Kabul",
  "Asia/Kamchatka",
  "Asia/Karachi",
  "Asia/Kathmandu",
  "Asia/Khandyga",
  "Asia/Kolkata",
  "Asia/Krasnoyarsk",
  "Asia/Kuching",
  "Asia/Macau",
  "Asia/Magadan",
  "Asia/Makassar",
  "Asia/Manila",
  "Asia/Nicosia",
  "Asia/Novokuznetsk",
  "Asia/Novosibirsk",
  "Asia/Omsk",
  "Asia/Oral",
  "Asia/Pontianak",
  "Asia/Pyongyang",
  "Asia/Qatar",
  "Asia/Qostanay",
  "Asia/Qyzylorda",
  "Asia/Riyadh",
  "Asia/Sakhalin",
  "Asia/Samarkand",
  "Asia/Seoul",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Srednekolymsk",
  "Asia/Taipei",
  "Asia/Tashkent",
  "Asia/Tbilisi",
  "Asia/Tehran",
  "Asia/Thimphu",
  "Asia/Tokyo",
  "Asia/Tomsk",
  "Asia/Ulaanbaatar",
  "Asia/Urumqi",
  "Asia/Ust-Nera",
  "Asia/Vladivostok",
  "Asia/Yakutsk",
  "Asia/Yangon",
  "Asia/Yekaterinburg",
  "Asia/Yerevan",
  "Atlantic/Azores",
  "Atlantic/Bermuda",
  "Atlantic/Canary",
  "Atlantic/Cape_Verde",
  "Atlantic/Faroe",
  "Atlantic/Madeira",
  "Atlantic/South_Georgia",
  "Atlantic/Stanley",
  "Australia/Adelaide",
  "Australia/Brisbane",
  "Australia/Broken_Hill",
  "Australia/Darwin",
  "Australia/Eucla",
  "Australia/Hobart",
  "Australia/Lindeman",
  "Australia/Lord_Howe",
  "Australia/Melbourne",
  "Australia/Perth",
  "Australia/Sydney",
  "Europe/Andorra",
  "Europe/Astrakhan",
  "Europe/Athens",
  "Europe/Belgrade",
  "Europe/Berlin",
  "Europe/Brussels",
  "Europe/Bucharest",
  "Europe/Budapest",
  "Europe/Chisinau",
  "Europe/Dublin",
  "Europe/Gibraltar",
  "Europe/Helsinki",
  "Europe/Istanbul",
  "Europe/Kaliningrad",
  "Europe/Kirov",
  "Europe/Kyiv",
  "Europe/Lisbon",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Malta",
  "Europe/Minsk",
  "Europe/Moscow",
  "Europe/Paris",
  "Europe/Prague",
  "Europe/Riga",
  "Europe/Rome",
  "Europe/Samara",
  "Europe/Saratov",
  "Europe/Simferopol",
  "Europe/Sofia",
  "Europe/Tallinn",
  "Europe/Tirane",
  "Europe/Ulyanovsk",
  "Europe/Vienna",
  "Europe/Vilnius",
  "Europe/Volgograd",
  "Europe/Warsaw",
  "Europe/Zurich",
  "Indian/Chagos",
  "Indian/Maldives",
  "Indian/Mauritius",
  "Pacific/Apia",
  "Pacific/Auckland",
  "Pacific/Bougainville",
  "Pacific/Chatham",
  "Pacific/Easter",
  "Pacific/Efate",
  "Pacific/Fakaofo",
  "Pacific/Fiji",
  "Pacific/Galapagos",
  "Pacific/Gambier",
  "Pacific/Guadalcanal",
  "Pacific/Guam",
  "Pacific/Honolulu",
  "Pacific/Kanton",
  "Pacific/Kiritimati",
  "Pacific/Kosrae",
  "Pacific/Kwajalein",
  "Pacific/Marquesas",
  "Pacific/Nauru",
  "Pacific/Niue",
  "Pacific/Norfolk",
  "Pacific/Noumea",
  "Pacific/Pago_Pago",
  "Pacific/Palau",
  "Pacific/Pitcairn",
  "Pacific/Port_Moresby",
  "Pacific/Rarotonga",
  "Pacific/Tahiti",
  "Pacific/Tarawa",
  "Pacific/Tongatapu",
];
interface Props {
  ventId: string;
  setTabDescription: (name: string) => void;
}

const VentConfigTab = (props: Props) => {
  const ventId = props.ventId;

  const [_] = useLocation();
  const { data: vent, hasChanges } = useAppSelector(
    (state: RootState) => state.ventConfigTab,
  );
  const {
    data: ventResponse,
    isFetching,
    isError: isErrorFetchingVent,
    error: ventError,
  } = useGetVentQuery({ id: ventId });
  const [deleteHost] = useDeleteHostMutation();
  const dispatch = useAppDispatch();
  const [editorKey, setEditorKey] = useState(0); // Key to force re-render of Editor

  useEffect(() => {
    if (ventResponse) {
      dispatch(initialize(ventResponse));
      props.setTabDescription(ventResponse.name);
    }
  }, [ventResponse]);
  useEffect(() => {
    // During development useEffect is called twice by React.
    // We need to protect against that by returning if hasChanges didn't change
    if (IsBlockNav() === hasChanges) return;
    if (hasChanges) {
      SetBlockNav(true);
    } else {
      // Can happen if we support undo
      SetBlockNav(false);
    }
  }, [hasChanges]);
  const [openedEditHost, { open: openEditHost, close: closeEditHost }] =
    useDisclosure(false);
  const [openedCreateHost, { open: openCreateHost, close: closeCreateHost }] =
    useDisclosure(false);
  const [selectedHostEmail, setSelectedHostEmail] = useState<string | null>(
    null,
  );

  const nameRef = useRef<HTMLInputElement>(null);
  const [saveVent, { isLoading: isSavingVent }] = useSaveVentMutation();

  const handleDeleteHost = async (hostEmail: string) => {
    if (vent == null) {
      console.error("null");
      return;
    }

    const request: DeleteHostRequest = {
      ventId: vent.id,
      hostEmail,
      updateTime: vent.updateTime!,
    };

    try {
      await deleteHost(request).unwrap();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  const handleSave = async () => {
    if (vent === undefined) {
      console.error("vent is undefined but should not be");
      return;
    }
    if (vent.name == null) {
      displayErrorMessage("Name is required.");
      if (nameRef.current) nameRef.current.focus();
      return;
    }
    const now = localDateToServer(new Date());
    const request: SaveVentRequest = {
      id: vent.id!,
      name: vent.name,
      starts: vent.starts,
      ends: vent.ends,
      description: vent.description,
      descriptionHtml: vent.descriptionHtml,
      secondaryMax: vent.secondaryMax,
      allowGuestsView: vent.allowGuestsView,
      excludeUndecidedGuests: vent.excludeUndecidedGuests,
      excludeDeclinedGuests: vent.excludeDeclinedGuests,
      allowPublicRsvps: vent.allowPublicRsvps,
      rsvpStart: vent.rsvpStart === "" ? now : vent.rsvpStart,
      rsvpEnd: vent.rsvpEnd === "" ? vent.starts : vent.rsvpEnd,
      rsvpStartDefault: vent.rsvpStartDefault,
      rsvpEndDefault: vent.rsvpEndDefault,
      location: vent.location,
      locationUrl: vent.locationUrl,
      timeZone: vent.timeZone,
      allowGuestMessaging: vent.allowGuestMessaging,
      acceptedGuestLimit: vent.acceptedGuestLimit,
      updateTime: vent.updateTime!,
    };

    try {
      await saveVent(request).unwrap();
      dispatch(reset());
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (isErrorFetchingVent) {
    handleFetchingError(ventError);
    return null;
  }
  if (isFetching || !vent || !ventResponse) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  let hostRows: JSX.Element[] = [];
  const hostEmails = Object.keys(ventResponse.hostRecords);
  hostRows = hostEmails.map((hostEmail) => {
    const hostRecord = ventResponse.hostRecords[hostEmail];
    return (
      <Table.Tr key={hostEmail}>
        <Table.Td>
          <Group gap="0.5rem">
            <EbTooltip label="Edit host permissions">
              <EbActionIcon
                disabled={!ventResponse.canUserEdit || hasChanges}
                aria-label="Edit host permissions"
                onClick={() => {
                  setSelectedHostEmail(hostEmail);
                  openEditHost();
                }}
              >
                <IconEdit />
              </EbActionIcon>
            </EbTooltip>
            <EbTooltip label="Delete host">
              <EbActionIcon
                disabled={!ventResponse.canUserEdit || hasChanges}
                aria-label="Delete host"
                onClick={() => {
                  openConfirmationModal({
                    message: `Please confirm deletion of the host ${hostRecord.name}`,
                    title: "Delete Host",
                    onConfirm: () => handleDeleteHost(hostEmail),
                  });
                }}
              >
                <IconDelete />
              </EbActionIcon>
            </EbTooltip>
          </Group>
        </Table.Td>
        <Table.Td>{hostRecord.name}</Table.Td>
        <Table.Td>{hostRecord.email}</Table.Td>
        <Table.Td>{formatPerm(hostRecord.perm)}</Table.Td>
      </Table.Tr>
    );
  });

  const selectedHost = selectedHostEmail
    ? ventResponse.hostRecords[selectedHostEmail]
    : null;
  return (
    <>
      <AppShell.Main>
        <Modal
          opened={openedEditHost}
          onClose={closeEditHost}
          withCloseButton={false}
          centered
        >
          <EditHost
            onClose={closeEditHost}
            ventId={ventId}
            hostEmail={selectedHost?.email!}
            hostName={selectedHost?.name!}
            hostPerm={selectedHost?.perm!}
            hostEmailOptOut={selectedHost?.emailOptOut!}
            userPerm={ventResponse.userHostPerm!}
            updateTime={ventResponse.updateTime!}
          />
        </Modal>
        <Modal
          opened={openedCreateHost}
          onClose={closeCreateHost}
          withCloseButton={false}
          centered
        >
          <CreateHost
            onClose={closeCreateHost}
            ventId={ventId}
            ventName={ventResponse.name}
            userPerm={ventResponse.userHostPerm!}
            updateTime={ventResponse.updateTime!}
          />
        </Modal>
        <TabHeader
          label="Configuration for"
          ventId={ventId}
          ventName={ventResponse.name}
        />
        <Fieldset legend="Configuration">
          <Stack gap="xs">
            <TextInput
              disabled={!ventResponse?.canUserEdit}
              maxLength={50}
              label="Event name"
              style={{ width: "300px" }}
              required
              ref={nameRef}
              value={vent?.name ?? ""}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "name",
                    value: event.currentTarget.value,
                  }),
                )
              }
            />

            <Textarea
              disabled={!ventResponse?.canUserEdit}
              mt="0"
              maxLength={200}
              style={{ width: "300px" }}
              rows={3}
              label="Location"
              value={vent.location ?? ""}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "location",
                    value: event.currentTarget.value,
                  }),
                )
              }
            />

            <TextInput
              disabled={!ventResponse?.canUserEdit}
              maxLength={2048}
              label="Location URL"
              style={{ width: "300px" }}
              value={vent?.locationUrl ?? ""}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "locationUrl",
                    value: event.currentTarget.value,
                  }),
                )
              }
            />
          </Stack>
          <Group>
            <DateTimePicker
              timePickerProps={{ format: "12h" }}
              disabled={!ventResponse?.canUserEdit}
              style={{ width: "200px" }}
              required
              mt="md"
              label="Starts at"
              placeholder="Pick start date and time"
              valueFormat="DD MMM YYYY hh:mm A z"
              value={new Date(vent.starts)}
              onChange={(event) => {
                const starts = dayjs(vent.starts);
                const ends = dayjs(vent.ends);
                const diff = ends.diff(starts);
                const newStarts = dayjs(event);
                const newEnds = newStarts.add(diff);
                dispatch(
                  updateField({
                    field: "starts",
                    value: convertToIso8601WithLocalTimezone(
                      event ?? undefined,
                    ),
                  }),
                );
                dispatch(
                  updateField({
                    field: "ends",
                    value: newEnds.toISOString(),
                  }),
                );
              }}
            />
            <DateTimePicker
              timePickerProps={{ format: "12h" }}
              disabled={!ventResponse?.canUserEdit}
              style={{ width: "200px" }}
              required
              mt="md"
              label="Ends at"
              placeholder="Pick end date and time"
              valueFormat="DD MMM YYYY hh:mm A z"
              value={new Date(vent.ends)}
              onChange={(event) => {
                dispatch(
                  updateField({
                    field: "ends",
                    value: convertToIso8601WithLocalTimezone(
                      event ?? undefined,
                    ),
                  }),
                );
              }}
            />
          </Group>
          <Text size="xs" c="dimmed">
            Tip: changing the start changes the end by the same amount
          </Text>
          <Group>
            <DateTimePicker
              timePickerProps={{ format: "12h" }}
              disabled={!ventResponse?.canUserEdit}
              style={{ width: "200px" }}
              mt="md"
              clearable
              label={
                <LabelWithTooltipHelp
                  helpText="Leave blank to start RSVPs now"
                  labelText="RSVP starts at"
                />
              }
              placeholder="Leave blank to start RSVPs now"
              valueFormat="DD MMM YYYY hh:mm A z"
              value={
                vent.rsvpStartDefault ? undefined : new Date(vent.rsvpStart)
              }
              onChange={(event) => {
                dispatch(
                  updateField({
                    field: "rsvpStart",
                    value: convertToIso8601WithLocalTimezone(
                      event ?? undefined,
                    ),
                  }),
                );
                dispatch(
                  updateField({
                    field: "rsvpStartDefault",
                    value: event == null ? true : false,
                  }),
                );
              }}
            />
            <DateTimePicker
              timePickerProps={{ format: "12h" }}
              disabled={!ventResponse?.canUserEdit}
              style={{ width: "200px" }}
              mt="md"
              clearable
              label={
                <LabelWithTooltipHelp
                  helpText="Leave blank to end RSVPs when event starts"
                  labelText="RSVP ends at"
                />
              }
              placeholder="Leave blank to end RSVPs when event starts"
              valueFormat="DD MMM YYYY hh:mm A z"
              value={vent.rsvpEndDefault ? undefined : new Date(vent.rsvpEnd)}
              onChange={(event) => {
                dispatch(
                  updateField({
                    field: "rsvpEnd",
                    value: convertToIso8601WithLocalTimezone(
                      event ?? undefined,
                    ),
                  }),
                );
                dispatch(
                  updateField({
                    field: "rsvpEndDefault",
                    value: event == null ? true : false,
                  }),
                );
              }}
            />
          </Group>
          <Text mt="sm" size="sm" fw={500}>
            Event Time Zone (determines time zone displayed in automated emails,
            not on web pages)
          </Text>
          <Autocomplete
            style={{ width: "300px" }}
            data={timeZones}
            value={vent.timeZone}
            onChange={(v) => {
              dispatch(
                updateField({
                  field: "timeZone",
                  value: v,
                }),
              );
            }}
          />
          <Paper mt="sm">
            <Editor
              key={editorKey}
              label="Description (appears on event home page)"
              disabled={!ventResponse?.canUserEdit}
              maxLength={10000}
              value={vent.descriptionHtml ?? ""}
              onChange={(htmlContent, textContent) => {
                dispatch(
                  updateField({
                    field: "description",
                    value: textContent,
                  }),
                );
                dispatch(
                  updateField({
                    field: "descriptionHtml",
                    value: htmlContent,
                  }),
                );
              }}
            ></Editor>
          </Paper>
          <Fieldset legend="Guest Management">
            <Switch
              disabled={!ventResponse?.canUserEdit}
              label="Allow anyone to RSVP (not just guests you entered)"
              mt="md"
              checked={vent.allowPublicRsvps}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "allowPublicRsvps",
                    value: event.currentTarget.checked,
                  }),
                )
              }
            />
            <NumberInput
              disabled={!ventResponse?.canUserEdit}
              style={{ width: "200px" }}
              wrapperProps={{ style: { pointerEvents: "auto" } }}
              labelProps={{ style: { pointerEvents: "auto" } }}
              styles={{
                label: { whiteSpace: "nowrap", pointerEvents: "auto" }, // Prevent label wrapping
              }}
              min={0}
              maxLength={6}
              mt="md"
              allowNegative={false}
              allowDecimal={false}
              label={
                <LabelWithTooltipHelp
                  helpText="This is the maximum number of guests that can accept. The limit does not apply when hosts mark guests as accepted. Set to 0 or blank for no limit."
                  labelText="Accepted guests allowed"
                />
              }
              value={vent.acceptedGuestLimit}
              onChange={(value) => {
                dispatch(
                  updateField({
                    field: "acceptedGuestLimit",
                    value: value === "" ? 0 : value,
                  }),
                );
              }}
            />
            <NumberInput
              disabled={!ventResponse?.canUserEdit}
              style={{ width: "200px" }}
              styles={{
                label: { whiteSpace: "nowrap" }, // Prevent label wrapping
              }}
              min={0}
              max={25}
              maxLength={3}
              mt="md"
              allowNegative={false}
              allowDecimal={false}
              label="Plus ones allowed per guest"
              value={vent.secondaryMax}
              onChange={(value) => {
                dispatch(
                  updateField({
                    field: "secondaryMax",
                    value: value === "" ? 0 : value,
                  }),
                );
              }}
            />
          </Fieldset>
          <Fieldset legend="Guest View Options">
            <Switch
              disabled={!ventResponse?.canUserEdit}
              label="Allow guests to view other guests and their activities (but not their email addresses)"
              mt="0"
              checked={vent.allowGuestsView}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "allowGuestsView",
                    value: event.currentTarget.checked,
                  }),
                )
              }
            />
            <Fieldset legend="Exceptions">
              <Switch
                disabled={!ventResponse?.canUserEdit}
                label="Exclude undecided guests"
                mt="0"
                checked={vent.excludeUndecidedGuests}
                onChange={(event) =>
                  dispatch(
                    updateField({
                      field: "excludeUndecidedGuests",
                      value: event.currentTarget.checked,
                    }),
                  )
                }
              />

              <Switch
                disabled={!ventResponse?.canUserEdit}
                label="Exclude declined guests"
                mt="md"
                checked={vent.excludeDeclinedGuests}
                onChange={(event) =>
                  dispatch(
                    updateField({
                      field: "excludeDeclinedGuests",
                      value: event.currentTarget.checked,
                    }),
                  )
                }
              />
            </Fieldset>
            <Switch
              disabled={!ventResponse?.canUserEdit}
              label="Allow guests to message other guests privately (without sharing email addresses)"
              description="You must allow guests to view other guests for this to work"
              mt="md"
              checked={vent.allowGuestMessaging}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "allowGuestMessaging",
                    value: event.currentTarget.checked,
                  }),
                )
              }
            />
          </Fieldset>

          <div>
            <Button
              mt="xs"
              mr="md"
              onClick={() => {
                dispatch(reset());
                setEditorKey((prev) => prev + 1); // Force re-render of Editor to reset content
              }}
              disabled={!hasChanges}
            >
              Discard Changes
            </Button>
            <Button
              mt="xs"
              onClick={handleSave}
              disabled={!hasChanges || isSavingVent}
              rightSection={isSavingVent ? <Loader size={14} /> : null}
            >
              Save Configuration
            </Button>
          </div>
        </Fieldset>
        <Fieldset legend="Shortcuts">
          <Group gap="xs" align="flex-start">
            <NavLink
              component={Link}
              label="Guests"
              leftSection={<IconGuests />}
              href={getLinkToVentGuests(props.ventId)}
              active
              style={{
                width: "fit-content",
              }}
            />
            <NavLink
              component={Link}
              label="Activities"
              leftSection={<IconActivities />}
              href={getLinkToVentActivities(props.ventId)}
              active
              style={{
                width: "fit-content",
              }}
            />
            <NavLink
              component={Link}
              label="Email Templates"
              leftSection={<IconEmailConfiguration />}
              href={getLinkToVentEmailTemplates(props.ventId)}
              active
              style={{
                width: "fit-content",
              }}
            />
            <NavLink
              component={Link}
              label="Custom Fields"
              leftSection={<IconCustomField />}
              href={getLinkToVentCustomFields(props.ventId)}
              active
              style={{
                width: "fit-content",
              }}
            />
            <NavLink
              component={Link}
              label="Event Home"
              leftSection={<IconEventHome />}
              href={getLinkToVentHome(props.ventId)}
              active
              style={{
                width: "fit-content",
              }}
            />
          </Group>
        </Fieldset>
        <Fieldset
          legend={
            "Hosts" +
            (hasChanges ? " (Save configuration changes to work on hosts)" : "")
          }
        >
          <Button
            onClick={openCreateHost}
            disabled={hasChanges || !ventResponse.canUserEdit}
            rightSection={<IconAddNew />}
          >
            Add Host
          </Button>
          <Skeleton visible={ventResponse == null}>
            <div hidden={ventResponse == null || hostRows.length === 0}>
              <Table.ScrollContainer minWidth={500}>
                <Table stickyHeader stickyHeaderOffset={0}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Actions</Table.Th>
                      <Table.Th>Host Name</Table.Th>
                      <Table.Th>Host Email</Table.Th>
                      <Table.Th>Host Permissions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>{hostRows}</Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </div>
          </Skeleton>
        </Fieldset>
      </AppShell.Main>
    </>
  );
};

export default VentConfigTab;
